<script>
/**
 * @component ContractList
 * @desc Lista de contratos médicos con filtros por prestador y estado.
 * Permite crear nuevos contratos y acceder al detalle de cada uno.
 */
import medicalContractService from "@/services/medical-contract";
import { mapGetters } from "vuex";

const STATUS_LABELS = {
  draft: "Borrador",
  active: "Activo",
  expired: "Vencido",
  terminated: "Rescindido",
};

const STATUS_TYPES = {
  draft: "info",
  active: "success",
  expired: "warning",
  terminated: "danger",
};

export default {
  name: "ContractList",
  computed: { ...mapGetters(["isAdmin"]) },
  data() {
    return {
      contracts: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      filterStatus: "",
    };
  },
  created() {
    this.fetchContracts();
  },
  methods: {
    /**
     * @method fetchContracts
     * @desc Carga los contratos con filtros y paginación
     */
    async fetchContracts() {
      this.loading = true;
      try {
        const params = {
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.filterStatus) params.status = this.filterStatus;
        const result = await medicalContractService.query(params);
        this.contracts = result.docs || [];
        this.total = result.total || 0;
      } catch {
        this.$message.error("Error al cargar contratos");
      } finally {
        this.loading = false;
      }
    },
    /** @method getStatusLabel */
    getStatusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    /** @method getStatusType */
    getStatusType(status) {
      return STATUS_TYPES[status] || "info";
    },
    /** @method onPageChange */
    onPageChange(page) {
      this.page = page;
      this.fetchContracts();
    },
    /** @method goToDetail */
    goToDetail(contract) {
      this.$router.push({
        name: "contract-detail",
        params: { id: contract._id },
      });
    },
    /** @method goToNew */
    goToNew() {
      this.$router.push({ name: "contract-new" });
    },
    /** @method formatDate */
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("es-AR") : "-";
    },
    /**
     * @method providerCode
     * @desc UC-REG-220 — N° prestador (PR-NNNNNN) del prestador del contrato.
     * @param {Object} row - Contrato
     * @returns {string}
     */
    providerCode(row) {
      const p = row.provider;
      return (p && p.doctor && p.doctor.providerCode) || "-";
    },
    /**
     * @method providerName
     * @desc UC-REG-220 — Nombre del prestador en Capital Case (los nombres se
     *   cargan en minúsculas y quedaban "federico ares" en la grilla).
     * @param {Object} row - Contrato
     * @returns {string}
     */
    providerName(row) {
      const p = row.provider;
      if (!p) return "-";
      const full = `${p.firstName || ""} ${p.lastName || ""}`
        .trim()
        .split(/\s+/)
        .map((w) =>
          w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w
        )
        .join(" ");
      return full || "-";
    },
  },
};
</script>

<template lang="pug">
div
  .page-header
    h1.page-title Contratos médicos
    el-button(v-if="isAdmin" type="primary" icon="el-icon-plus" @click="goToNew") Nuevo contrato

  .filters-bar
    el-select(
      v-model="filterStatus"
      placeholder="Todos los estados"
      clearable
      size="small"
      @change="fetchContracts"
      style="width: 200px"
    )
      el-option(label="Borrador" value="draft")
      el-option(label="Activo" value="active")
      el-option(label="Vencido" value="expired")
      el-option(label="Rescindido" value="terminated")

  el-table(
    :data="contracts"
    v-loading="loading"
    border
    style="margin-top: 16px"
    @row-click="goToDetail"
  )
    el-table-column(label="N° Contrato" prop="contractNumber" width="130")
      template(slot-scope="{ row }")
        | {{ row.contractNumber || '-' }}
    el-table-column(label="N° Prestador" width="140")
      template(slot-scope="{ row }")
        | {{ providerCode(row) }}
    el-table-column(label="Prestador")
      template(slot-scope="{ row }")
        | {{ providerName(row) }}
    el-table-column(label="Inicio" width="110")
      template(slot-scope="{ row }") {{ formatDate(row.startsAt) }}
    el-table-column(label="Vencimiento" width="110")
      template(slot-scope="{ row }") {{ formatDate(row.endsAt) }}
    el-table-column(label="Modalidad" width="150")
      template(slot-scope="{ row }")
        | {{ { 'per-practice': 'Por prestación', monthly: 'Mensual', mixed: 'Mixto' }[row.paymentModality] || '-' }}
    el-table-column(label="Estado" width="120")
      template(slot-scope="{ row }")
        el-tag(:type="getStatusType(row.status)" size="small") {{ getStatusLabel(row.status) }}

  .pagination-wrapper(style="margin-top: 16px; text-align: center")
    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next, total"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      @current-change="onPageChange"
    )
</template>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
.filters-bar {
  display: flex;
  gap: 10px;
}
</style>
